import { useEffect, useState, useRef } from "react";
import { Upload, Search, Trash2, Pencil, Copy, ExternalLink, FileText, PlayCircle, Image as ImageIcon, FolderOpen } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { fmtDate  } from "../../lib/format";
import { errText } from "../../lib/errors";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const inputCls = "h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30";

export default function AdminMedia() {
  const [files, setFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [cat, setCat] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [upMeta, setUpMeta] = useState({ category: "", description: "" });
  const [edit, setEdit] = useState(null);
  const [page, setPage] = useState(0);
  const fileRef = useRef(null);
  const PAGE_SIZE = 24;
  const pageCount = Math.max(1, Math.ceil(files.length / PAGE_SIZE));
  const pageFiles = files.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const load = () => {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (type) qs.set("type", type);
    if (cat) qs.set("category", cat);
    api.get(`/admin/media?${qs.toString()}`).then(({ data }) => { setFiles(data.files); setCategories(data.categories); }).catch(() => {});
  };
  useEffect(() => { setPage(0); }, [q, type, cat]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, type, cat]);

  const uploadFiles = async (list) => {
    if (!list.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of list) {
      const fd = new FormData();
      fd.append("file", file);
      if (upMeta.category) fd.append("category", upMeta.category);
      if (upMeta.description) fd.append("description", upMeta.description);
      try { await api.post("/admin/upload", fd); ok++; }
      catch (err) { toast.error(`${file.name}: ${errText(err) || "yüklenemedi"}`); }
    }
    if (ok) toast.success(`${ok} dosya yüklendi`);
    setUploading(false);
    load();
  };

  const upload = async (e) => {
    await uploadFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    uploadFiles(Array.from(e.dataTransfer?.files || []));
  };

  const cleanUrl = (f) => {
    const ext = f.ext || (f.url || "").split(".").pop();
    return f.slug ? `${window.location.origin}/media/${f.slug}${ext ? `.${ext}` : ""}` : `${window.location.origin}${f.url}`;
  };

  const copyUrl = (f) => {
    navigator.clipboard.writeText(cleanUrl(f));
    toast.success(f.slug ? "Temiz URL kopyalandı" : "URL kopyalandı");
  };

  const del = async (f) => {
    try {
      const { data } = await api.delete(`/admin/media/${f.file_id}`);
      if (!data.ok && data.used_in) {
        if (window.confirm(`Bu dosya kullanımda: ${data.used_in.join(", ")}. Yine de silinsin mi?`)) {
          await api.delete(`/admin/media/${f.file_id}?force=true`);
          toast.success("Dosya silindi (kullanım alanları etkilenebilir)");
        } else return;
      } else toast.success("Dosya silindi");
      load();
    } catch { toast.error("Silinemedi"); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/media/${edit.file_id}`, { original_filename: edit.original_filename, description: edit.description, category: edit.category, slug: edit.slug });
      toast.success("Dosya bilgileri güncellendi");
      setEdit(null);
      load();
    } catch (e) { toast.error(errText(e, "Güncellenemedi")); }
  };

  const fmtSize = (s) => (s > 1048576 ? `${(s / 1048576).toFixed(1)} MB` : `${Math.round(s / 1024)} KB`);

  return (
    <div data-testid="admin-media-page" onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={(e) => { if (e.currentTarget === e.target) setDragging(false); }} onDrop={onDrop} className="relative">
      {dragging && (
        <div data-testid="media-drop-overlay" className="fixed inset-0 z-50 bg-forest/20 backdrop-blur-sm flex items-center justify-center pointer-events-none">
          <p className="bg-white rounded-2xl px-8 py-5 font-semibold text-forest shadow-xl flex items-center gap-3"><Upload className="w-5 h-5" /> Dosyaları buraya bırakın</p>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-forest">Ortam Kütüphanesi</h1>
          <p className="text-stone-500 text-sm mt-1">Tüm görsel, video ve PDF dosyaları — ürün, kategori, blog ve sayfalarda tekrar kullanılabilir.</p>
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="media-upload-category" className={`${inputCls} w-36`} placeholder="Klasör/Kategori" value={upMeta.category} onChange={(e) => setUpMeta({ ...upMeta, category: e.target.value })} />
          <button data-testid="media-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">
            <Upload className="w-4 h-4" /> {uploading ? "Yükleniyor..." : "Dosya Yükle"}
          </button>
          <input ref={fileRef} type="file" multiple accept="image/*,video/mp4,video/webm,video/quicktime,application/pdf" className="hidden" onChange={upload} data-testid="media-file-input" />
        </div>
      </div>

      <div data-testid="media-type-tabs" className="flex gap-2 mt-5 flex-wrap">
        {[["", "Tümü"], ["image", "Görseller"], ["video", "Videolar"], ["pdf", "PDF'ler"]].map(([v, l]) => (
          <button key={v} data-testid={`media-tab-${v || "all"}`} onClick={() => setType(v)} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${type === v ? "bg-forest text-white" : "bg-white border border-stone-200 text-stone-600 hover:bg-stone-50"}`}>{l}</button>
        ))}
      </div>

      <div className="flex gap-3 mt-4 flex-wrap">
        <div className="relative flex-1 min-w-52">
          <input data-testid="media-search" className={`${inputCls} w-full pl-10`} placeholder="Dosya adı veya açıklama ara..." value={q} onChange={(e) => setQ(e.target.value)} />
          <Search className="w-4 h-4 text-stone-400 absolute left-3.5 top-3.5" />
        </div>
        <select data-testid="media-category-filter" className={inputCls} value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">Tüm Kategoriler</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div data-testid="media-dropzone" onClick={() => fileRef.current?.click()} className="mt-4 border-2 border-dashed border-stone-300 hover:border-forest rounded-2xl bg-white/60 px-6 py-5 text-center cursor-pointer transition-colors">
        <p className="text-sm text-stone-500"><span className="font-semibold text-forest">Dosyaları buraya sürükleyip bırakın</span> veya tıklayıp seçin — çoklu yükleme desteklenir{upMeta.category ? ` (kategori: ${upMeta.category})` : ""}</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 mt-6">
        {pageFiles.map((f) => (
          <div key={f.file_id} data-testid={`media-card-${f.file_id}`} className="bg-white rounded-2xl border border-stone-200/80 overflow-hidden shadow-sm group">
            <div className="aspect-square bg-cream-dark flex items-center justify-center relative">
              {f.type === "image" ? (
                <img src={f.url} alt={f.original_filename} loading="lazy" className="w-full h-full object-cover" />
              ) : f.type === "video" ? (
                <PlayCircle className="w-10 h-10 text-stone-400" />
              ) : (
                <FileText className="w-10 h-10 text-stone-400" />
              )}
              <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider bg-black/60 text-white rounded-full px-2 py-0.5">{f.type}</span>
            </div>
            <div className="p-3">
              <p className="text-xs font-semibold text-stone-800 truncate" title={f.original_filename}>{f.original_filename}</p>
              <p className="text-[10px] text-stone-400 mt-0.5">{fmtSize(f.size)} · {fmtDate(f.created_at)}{f.category ? ` · ${f.category}` : ""}</p>
              <div className="flex gap-1 mt-2.5">
                <a href={f.url} target="_blank" rel="noreferrer" data-testid={`media-view-${f.file_id}`} className="p-1.5 rounded-lg hover:bg-cream-dark text-stone-500" title="Görüntüle/İndir"><ExternalLink className="w-3.5 h-3.5" /></a>
                <button data-testid={`media-copy-${f.file_id}`} onClick={() => copyUrl(f)} className="p-1.5 rounded-lg hover:bg-cream-dark text-stone-500" title="URL Kopyala"><Copy className="w-3.5 h-3.5" /></button>
                <button data-testid={`media-edit-${f.file_id}`} onClick={() => setEdit({ ...f })} className="p-1.5 rounded-lg hover:bg-cream-dark text-stone-500" title="Düzenle"><Pencil className="w-3.5 h-3.5" /></button>
                <button data-testid={`media-delete-${f.file_id}`} onClick={() => del(f)} className="p-1.5 rounded-lg hover:bg-red-50 text-terra" title="Sil"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
        {files.length === 0 && (
          <div className="col-span-full bg-white rounded-2xl border border-stone-200/80 p-14 text-center">
            <FolderOpen className="w-10 h-10 text-stone-300 mx-auto" />
            <p className="text-stone-500 text-sm mt-3">Henüz dosya yok veya filtreye uyan sonuç bulunamadı.</p>
          </div>
        )}
      </div>

      {files.length > PAGE_SIZE && (
        <div data-testid="media-pagination" className="flex items-center justify-center gap-3 mt-6">
          <button type="button" data-testid="media-prev-page" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="text-sm font-semibold text-forest disabled:text-stone-300 px-4 py-2 rounded-xl border border-stone-200 hover:bg-stone-50 disabled:hover:bg-transparent transition-colors">← Önceki</button>
          <span data-testid="media-page-info" className="text-sm text-stone-500">Sayfa {page + 1} / {pageCount} • {files.length} dosya</span>
          <button type="button" data-testid="media-next-page" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1} className="text-sm font-semibold text-forest disabled:text-stone-300 px-4 py-2 rounded-xl border border-stone-200 hover:bg-stone-50 disabled:hover:bg-transparent transition-colors">Sonraki →</button>
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Dosya Bilgilerini Düzenle</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4 mt-2">
              {edit.type === "image" && <img src={edit.url} alt="" className="w-full h-40 object-cover rounded-xl" />}
              <div><label className="text-xs font-semibold text-stone-600">Dosya Adı</label><input data-testid="media-edit-name" className={`${inputCls} w-full mt-1`} value={edit.original_filename} onChange={(e) => setEdit({ ...edit, original_filename: e.target.value })} /></div>
              <div>
                <label className="text-xs font-semibold text-stone-600">Kısa Ad (Temiz URL)</label>
                <input data-testid="media-edit-slug" className={`${inputCls} w-full mt-1`} value={edit.slug || ""} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} placeholder="ornek: hero-bal" />
                {edit.slug && <p data-testid="media-edit-slug-preview" className="text-xs text-stone-500 mt-1 break-all">{window.location.origin}/media/{edit.slug}{edit.ext ? `.${edit.ext}` : ""}</p>}
              </div>
              <div><label className="text-xs font-semibold text-stone-600">Açıklama</label><input data-testid="media-edit-desc" className={`${inputCls} w-full mt-1`} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-stone-600">Kategori</label><input data-testid="media-edit-category" className={`${inputCls} w-full mt-1`} value={edit.category || ""} onChange={(e) => setEdit({ ...edit, category: e.target.value })} /></div>
              <button data-testid="media-edit-save" onClick={saveEdit} className="w-full bg-forest hover:bg-forest-dark text-white font-semibold py-3 rounded-xl transition-colors">Kaydet</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
